import React, { useState, useEffect } from "react";
import "../../assets/css/nlis.css";
import CustomAlert from "../general/customAlert";
import CustomTable from "../general/CustomTable";
import { getNlisHoldings } from "../../api/nlis";
import { useCompany } from "../../helpers/CompanyContext";

// widths here + the "#" column's 4% (set in CustomTable.js colgroup) sum to 100%,
// so the table locks to the viewport width instead of scrolling.
const columns = [
  { name: "RFID", value: "rfid", width: "19%" },
  { name: "NLIS ID", value: "NLIS_ID", width: "19%" },
  { name: "Vendor PIC", value: "Vendor_x0020_PIC", width: "15%" },
  { name: "NVD/Waybill", value: "NVD_x002F_Waybill", width: "15%" },
  { name: "Transfer Date", value: "Transfer_date", width: "15%" },
  { name: "Species", value: "Species", width: "13%" },
];

const HoldingsPage = () => {
  const { selectedCompany } = useCompany();

  const [holdings, setHoldings] = useState([]);
  const [fetchedAt, setFetchedAt] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isMsg, setIsMsg] = useState(false);
  const [msg, setMsg] = useState(null);
  const [isError, setIsError] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [visibleColumns, setVisibleColumns] = useState({});

  const rowsPerPage = 20;
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;

  const handlePageChange = (page) => setCurrentPage(page);

  const loadHoldings = async () => {
    setIsLoading(true);
    try {
      const res = await getNlisHoldings();
      const rows = res.data?.holdings || res.data || [];
      setHoldings(rows);
      setFetchedAt(res.data?.fetched_at || null);
      setTotalPages(Math.ceil(rows.length / rowsPerPage));
      setVisibleColumns(
        columns.reduce((acc, col) => {
          acc[col.name] = true;
          return acc;
        }, {})
      );
    } catch (err) {
      console.error("[NLIS Holdings] load error:", err);
      setIsError(true);
      setIsMsg(true);
      setMsg("Failed to load holdings from NLIS.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    setCurrentPage(1);
    loadHoldings();
  }, [selectedCompany]);

  return (
    <div className="nlis-page holdings-page">
      <h4 className="mb-3 text-center text-acumen-color">NLIS Current Holdings</h4>

      <div className="container-fluid">
        <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between mb-3">
          <div className="text-muted" style={{ fontSize: "0.875rem" }}>
            {fetchedAt
              ? `Last updated: ${new Date(fetchedAt).toLocaleString("en-AU")}`
              : holdings.length > 0
              ? "Last updated: unknown"
              : ""}
          </div>
        </div>

        {isLoading && (
          <div className="alert alert-info mt-4">Loading holdings...</div>
        )}

        {!isLoading && (
          <div className="mt-2">
            {holdings.length > 0 ? (
              <>
                <CustomTable
                  items={holdings.slice(startIndex, endIndex)}
                  columns={columns}
                  visibleColumns={visibleColumns}
                  setVisibleColumns={setVisibleColumns}
                  cantHideColumns={["rfid"]}
                  maxHeight="calc(100vh - 280px)"
                  horizontalMargin="mx-1"
                  fixedLayout
                />
                {totalPages > 1 && (
                  <div className="pagination mt-2">
                    <button
                      className="btn btn-acumen-color text-white me-2"
                      disabled={currentPage === 1}
                      onClick={() => handlePageChange(currentPage - 1)}>
                      Previous
                    </button>
                    {Array.from({ length: totalPages }, (_, index) => (
                      <button
                        key={index + 1}
                        className={`btn ${
                          currentPage === index + 1 ? "btn-secondary" : "btn-light"
                        } me-1`}
                        onClick={() => handlePageChange(index + 1)}>
                        {index + 1}
                      </button>
                    ))}
                    <button
                      className="btn btn-acumen-color text-white ms-2"
                      disabled={currentPage === totalPages}
                      onClick={() => handlePageChange(currentPage + 1)}>
                      Next
                    </button>
                  </div>
                )}
              </>
            ) : (
              !isError && !isLoading && (
                <div className="alert alert-warning mt-2">
                  No holdings data available.
                </div>
              )
            )}
          </div>
        )}
      </div>

      {isMsg && (
        <CustomAlert
          isError={isError}
          message={msg}
          showMessage={isMsg}
          setShowMessage={setIsMsg}
        />
      )}
    </div>
  );
};

export default HoldingsPage;